import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import {States} from "../../shared/config/constant.module";
import template from './wareHeldReport.tpl.html';
import {Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {StateParams} from "../../common";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import ReportServiceName, {ReportService} from "../../services/reports/report.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import { ICommonQueryingReport } from './commonQueryingReport';

let requires = [LoaderServiceName, ReportServiceName, NavigationServiceName];

interface WareHeldReportController extends ICommonQueryingReport {
  results: apsViewModels.WareHeldReportItem[];
  searchPerformed: boolean;

  filteredResults(): apsViewModels.WareHeldReportItem[];
  generateReport(): void;
  formatTime(time: Date): string;
  exportToCsv(): any[];
}

export interface WareHeldReportParams extends StateParams {
  reportPlantId: string;
  reportShopId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
}

class WareHeldReportCtrl implements WareHeldReportController {
  plants: Plant[];
  selectedPlant: Plant;
  isMissingSignOff: boolean;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;
  selectedShop: apsViewModels.ShopNavigation;
  requireShopSelection: boolean;
  uiStartDate: Date;
  uiEndDate: Date;
  requirePlantSelection: boolean;

  private $stateParams: WareHeldReportParams;
  private $state: angular.ui.IStateService;

  results: apsViewModels.WareHeldReportItem[];
  searchPerformed: boolean;

  loaderService: LoaderService;
  reportService: ReportService;
  private $q: angular.IQService;

  shifts: string[];
  selectedShift?: string;
  items: string[];
  selectedItem?: string;
  defects: string[];
  selectedDefect?: string;

  constructor(
    $stateParams: WareHeldReportParams,
    $state: angular.ui.IStateService,
    $q: angular.IQService,
    navigationService: NavigationService,
    reportService: ReportService,
    loaderService: LoaderService
  ) {
    this.$q = $q;
    this.$state = $state;
    this.$stateParams = $stateParams;
    this.loaderService = loaderService;
    this.reportService = reportService;

    this.requirePlantSelection = true;

    var today = moment().startOf('day');
    this.startDate = today.clone().subtract(1, 'month').toDate();
    this.endDate = today.toDate();

    var promise = navigationService.getNavigation().then((navModel) => {
      this.plants = navModel.plants();
      this.selectedPlant = navModel.selectedPlant;
      this.updatePlant();
      this.selectedShop = navModel.selectedShop;

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);
  }

  filteredResults(): apsViewModels.WareHeldReportItem[] {
    var results = _(this.results);
    if (this.selectedShift) {
      results = results.filter(x => x.shift === this.selectedShift);
    }
    if (this.selectedItem) {
      results = results.filter(x => x.item === this.selectedItem);
    }
    if (this.selectedDefect) {
      results = results.filter(x => x.defect === this.selectedDefect);
    }
    return results.value();
  }

  updateDropdowns(): void {
    this.shifts = _.uniq(_.filter(_.map(this.results, 'shift'), x => !!x));
    this.items = _.uniq(_.filter(_.map(this.results, 'item'), x => !!x));
    this.defects = _.uniq(_.filter(_.map(this.results, 'defect'), x => !!x));
  }

  updatePlant(): void {
    this.selectedShop = null;
  }

  loadState(params: WareHeldReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportPlantId !== params.plantId) {
      var plant = _.find(this.plants, {id: params.reportPlantId});
      if (plant) {
        this.selectedPlant = plant;
        let shop = _.find(plant.shops, {id: params.reportShopId});
        if (shop) {
          this.selectedPlant = plant;
          this.selectedShop = shop;
        }
      }
    }

    if (params.startDate) {
      var startDate = moment(params.startDate);
      if (startDate.isValid()) {
        this.startDate = startDate.toDate();
      }
    }

    if (params.endDate) {
      var endDate = moment(params.endDate);
      if (endDate.isValid()) {
        this.endDate = endDate.toDate();
      }
    }

    if (params.autoRun) {
      return this.runReport();
    }

    return this.$q.when();
  }

  saveState() {
    var reportPlantId = this.selectedPlant ? this.selectedPlant.id : undefined;
    if (reportPlantId === this.$stateParams.plantId) {
      reportPlantId = undefined;
    }
    let reportShopId = this.selectedShop ? this.selectedShop.id : undefined;
    if (reportShopId === this.$stateParams.shopId) {
      reportShopId = undefined;
    }
    var params: WareHeldReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: reportPlantId,
      reportShopId: reportShopId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true
    };

    this.$state.transitionTo(States.WARE_HELD_REPORT, params);
  }

  generateReport() {
    if (this.form.$invalid) {
      return;
    }

    this.saveState();

    this.loaderService.loadingPromise(this.runReport());
  }

  private runReport(): angular.IPromise<any> {
    var startDateStart = this.startDate;
    var endDateEnd = this.endDate;

    return this.reportService.getWareHeldReport(this.selectedPlant.id, this.selectedShop
      ? this.selectedShop.id : null, startDateStart, endDateEnd)
      .then(reportItems => {
        this.results = reportItems;
        this.searchPerformed = true;
        this.updateDropdowns();
      });
  }

  exportToCsv() {
    return _.map<apsViewModels.WareHeldReportItem, any>(this.filteredResults(), (x) => {
      return {
        plant: this.selectedPlant.name,
        shop: x.shop,
        dateTime: this.formatTime(x.scheduledOn),
        shift: x.shift,
        item: x.item,
        defect: x.defect,
        molds: x.molds,
        affectedPallets: x.affectedPallets,
        palletRange: x.palletRange,
        reprocessedPallets: x.reprocessedPallets,
        comment: x.comment
      };
    })
  }

  formatTime(time: Date): string {
    return DateUtilityService.formatTime(time, this.selectedPlant.timeZoneId);
  }
}

export default angular.module('aps.controller.wareHeldReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.WARE_HELD_REPORT,
      url: '/admin/reports/wareHeld?startDate&endDate&autoRun&reportPlantId&reportShopId',
      params: {
        startDate: {dynamic: true},
        endDate: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
        reportShopId: {dynamic: true}
      },
      controller: 'WareHeldReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Held Ware Report'
      }
    });
  })
  .controller('WareHeldReportCtrl', WareHeldReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/reports/wareHeldReport.controller.ts